import { Box } from '@mui/joy';
import { CellValueChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportPartTable } from 'bom/components';
import { ProblemNumber } from 'bom/elements';
import { ManufacturerValidationStatus, SolveProblemsStep } from 'bom/enums';
import { useBomImportActions, useGetBomImportNotValidParts } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { IImportNotValidPartsPayload, IImportPart } from 'bom/types';
import { mapUpsertImportPartsData } from 'bom/utils';
import { Button, Notification } from 'core/components';
import { NotificationCard, Title } from 'core/elements';
import { NotificationCardType, NotificationPosition } from 'core/enums';
import { AgGridClientTableProvider, useInnerStepsContext } from 'core/states';
import { GetManyResponse } from 'core/types';

import { getColumnDefs } from './columns';

const PartsWithoutManufacturers = ({ isValid = false }: { isValid: boolean }) => {
  const { t } = useTranslation();

  const { activeStep, goNext } = useInnerStepsContext();
  const { partImportId, problems, setProblems, isAlternateVersionEnabled } =
    useBomPartsImportContext();

  const allValid = useMemo(
    () => isValid || problems[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.unsolved === 0,
    [isValid, problems]
  );

  const payload: IImportNotValidPartsPayload = useMemo(
    () => ({
      filters: {
        importId: partImportId,
        manufacturerValidationStatus: ManufacturerValidationStatus.UNKNOWN,
        omit: '"0"',
      },
    }),
    [partImportId]
  );

  const enabled = useMemo(
    () => !isValid && activeStep?.type === SolveProblemsStep.ASSIGN_MANUFACTURERS,
    [isValid, activeStep]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetBomImportNotValidParts(payload, enabled, (data: GetManyResponse<IImportPart>) => {
    const records = data.records || [];

    setProblems((prev) => ({
      ...prev,
      [SolveProblemsStep.ASSIGN_MANUFACTURERS]: {
        solved: prev[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.unsolved - records.length,
        unsolved: records.length,
      },
    }));
  });

  const { upsertImportParts } = useBomImportActions();

  useEffect(() => {
    if (!allValid) {
      return;
    }

    setTimeout(() => {
      goNext();
    }, 1000);
  }, [allValid, goNext]);

  const mappedRecords = useMemo(
    () =>
      records.map((record) => ({
        id: record?.id,
        description: record?.description,
        number: record?.number,
        manufacturer: record?.manufacturerId
          ? { name: record?.manufacturer, id: record?.manufacturerId }
          : null,
        omit: false,
        doNotAssignManufacturer: false,
      })),
    [records]
  );

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMovable: true,
      enableRowGroup: false,
      maxWidth: 300,
    }),
    []
  );

  const columnDefs = useMemo(
    () => getColumnDefs(isAlternateVersionEnabled),
    [isAlternateVersionEnabled]
  );

  const onCellValueChanged = useCallback(
    async (_, { colDef: { field }, data, newValue, api }: CellValueChangedEvent) => {
      api.setGridOption('loading', true);

      const { success } = await upsertImportParts(
        mapUpsertImportPartsData([data], field, newValue)
      );

      if (!success) {
        api.setGridOption('loading', false);

        return;
      }

      setProblems((prev) => ({
        ...prev,
        [SolveProblemsStep.ASSIGN_MANUFACTURERS]: {
          solved: prev[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.solved + 1,
          unsolved: prev[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.unsolved - 1,
        },
      }));

      api.setGridOption('loading', false);

      return { removedRow: data, options: { redrawRows: false } };
    },
    [upsertImportParts, setProblems]
  );

  const content =
    isDataLoading || allValid ? (
      <>
        <Title text={t('titleAssignManufacturers')} />

        <NotificationCard
          title={isDataLoading ? t('titleStatus') : t('titleSuccess')}
          message={isDataLoading ? t('messagePartsValidating') : t('messagePartsValidated')}
          type={isDataLoading ? NotificationCardType.LOADING : NotificationCardType.SUCCESS}
        />
      </>
    ) : (
      <>
        <Title
          text={t('titleAssignManufacturers')}
          subtitle={
            <>
              <ProblemNumber
                number={
                  problems[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.unsolved || mappedRecords.length
                }
              />
              <ProblemNumber
                number={problems[SolveProblemsStep.ASSIGN_MANUFACTURERS]?.solved || 0}
                solved
              />
            </>
          }
        />

        <Notification position={NotificationPosition.DRAWER} />

        <AgGridClientTableProvider
          tableId="partsWithoutManufacturers"
          entityType="Part"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          data={mappedRecords}
          isDataLoading={isDataLoading}
          onCellValueChanged={onCellValueChanged}
          loadingPercentage={loadingPercentage}
          dataCurrentPage={currentPage}
          sideBar={{}}
          autoSizeStrategy={{
            type: 'fitCellContents',
          }}
          headerCheckbox={!isDataLoading}
        >
          <ImportPartTable
            filters={payload.filters}
            stepType={SolveProblemsStep.ASSIGN_MANUFACTURERS}
          />
        </AgGridClientTableProvider>
      </>
    );

  return (
    <>
      {content}

      <Box>
        <Button text={t('buttonLabelGoToTheNextStep')} onClick={goNext} disabled={!allValid} />
      </Box>
    </>
  );
};

export default PartsWithoutManufacturers;
